import React, { useState, useEffect } from "react";

const ScheduledDateSetter = () => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasExistingDate, setHasExistingDate] = useState(false);

  const studentId = localStorage.getItem("student_id");

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  useEffect(() => {
    if (!studentId) {
      showMessage("Student ID not found. Please log in again.", "error");
      setIsInitialLoad(false);
      return;
    }

    const fetchDate = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-scheduled-date/${studentId}`,
          {
            headers: { 
              "Content-Type": "application/json", 
              Authorization: `Bearer ${localStorage.getItem("token")}` 
            }
          }
        );
        const data = await res.json();

        if (res.ok && data.scheduled_date) {
          const dateTime = new Date(data.scheduled_date);
          setDate(dateTime.toISOString().split('T')[0]);
          setTime(dateTime.toTimeString().substring(0, 5));
          setHasExistingDate(true);
          showMessage("Interview date & time loaded", "success");
        } else {
          setHasExistingDate(false);
          showMessage("No interview scheduled yet", "info");
        }
      } catch (err) {
        showMessage("Unable to load interview details", "error");
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchDate();
  }, [studentId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!date || !time || !studentId) {
      showMessage("Please select both date and time", "error");
      return;
    }

    const scheduledDateTime = `${date}T${time}:00`;
    const selectedDateTime = new Date(scheduledDateTime);
    const now = new Date();

    if (selectedDateTime < now) {
      showMessage("Please select a future date and time", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/set-scheduled-date/${studentId}`, 
        {
          method: "PATCH",
          headers: { 
            "Content-Type": "application/json", 
            Authorization: `Bearer ${localStorage.getItem("token")}` 
          },
          body: JSON.stringify({ scheduled_date: scheduledDateTime }),
        }
      );

      const data = await res.json();

      if (res.ok) {
        setHasExistingDate(true);
        showMessage("Interview scheduled successfully!", "success");
      } else {
        showMessage(data.message || "Failed to schedule interview", "error");
      }
    } catch (err) {
      showMessage("Network error. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const getMessageStyles = () => {
    const baseStyles = "p-3 rounded-lg text-sm font-medium mb-4 border";
    switch (messageType) {
      case "success":
        return `${baseStyles} bg-green-50 text-green-800 border-green-200`;
      case "error":
        return `${baseStyles} bg-red-50 text-red-800 border-red-200`;
      default:
        return `${baseStyles} bg-blue-50 text-blue-800 border-blue-200`;
    }
  };

  const formatDisplayDateTime = () => {
    if (!date || !time) return "";
    return new Date(`${date}T${time}`).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isInitialLoad) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span className="text-gray-600 text-sm">Loading interview details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-blue-600 px-4 py-4">
        <div className="flex items-center space-x-2">
          <div className="bg-white bg-opacity-20 rounded p-2">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Interview Scheduling</h2>
            <p className="text-blue-100 text-xs">
              {hasExistingDate ? "Update schedule" : "Schedule interview"}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Status Message */}
        {message && (
          <div className={getMessageStyles()}>
            <div className="flex items-center space-x-2">
              {messageType === "success" && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {messageType === "error" && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              {messageType === "info" && (
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              )}
              <span className="text-sm">{message}</span>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Interview Date & Time
            </label>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time</label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            
            {/* Preview */}
            {date && time && (
              <div className="mt-3 p-2 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs text-gray-600">Selected:</p>
                <p className="font-medium text-blue-600 text-sm">{formatDisplayDateTime()}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-2">
            <button
              type="submit"
              disabled={loading || !date || !time}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-1">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{hasExistingDate ? "Update" : "Schedule"}</span>
                </div>
              )}
            </button>
            
            {hasExistingDate && (
              <button
                type="button"
                onClick={() => {
                  setDate("");
                  setTime("09:00");
                  showMessage("Schedule cleared", "info");
                }}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduledDateSetter;